import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ArrowLeft, ArrowRightLeft, ClipboardCheck } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field } from "@/components/shared/form";
import { PageHeader } from "@/components/shared/page-header";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useBatchGenealogy,
  useBatchGoodsReceipts,
  useInventoryBatch,
  useInventoryMovements,
  useTransferBatch,
} from "@/features/inventory/api";
import {
  BATCH_STATUSES,
  MOVEMENT_KINDS,
  QUALITY_STATUSES,
  batchStatusTone,
  fmtQty,
  labelFor,
  qualityTone,
} from "@/features/inventory/constants";
import { useStockLocations } from "@/features/locations/api";
import type { QcQueueRow } from "@/features/quality/types";
import { VerifyQualitySheet } from "@/features/quality/verify-sheet";
import { isSupabaseConfigured } from "@/lib/supabase";

export function InventoryBatchDetailPage({ batchId }: { batchId: string }) {
  return (
    <ErrorBoundary title="Batch detail crashed">
      <BatchDetailInner batchId={batchId} />
    </ErrorBoundary>
  );
}

function BatchDetailInner({ batchId }: { batchId: string }) {
  const [qcOpen, setQcOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [toLocationId, setToLocationId] = useState("");
  const { data: batch, isLoading, error } = useInventoryBatch(batchId);
  const { data: movements = [], isLoading: movesLoading } = useInventoryMovements(batchId);
  const { data: receipts = [] } = useBatchGoodsReceipts(batchId);
  const { data: genealogy } = useBatchGenealogy(batchId);
  const { data: locations = [] } = useStockLocations();
  const transfer = useTransferBatch();

  if (!isSupabaseConfigured) {
    return (
      <EmptyState title="Supabase not configured" description="Configure Supabase to view batches." />
    );
  }

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (error || !batch) {
    return (
      <EmptyState
        title="Batch not found"
        description={(error as Error)?.message ?? "Unknown batch"}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/inventory">Back to inventory</Link>
          </Button>
        }
      />
    );
  }

  const dims = [
    batch.thickness != null ? `Thk ${fmtQty(batch.thickness)}` : null,
    batch.width != null ? `W ${fmtQty(batch.width)}` : null,
    batch.length != null ? `L ${fmtQty(batch.length)}` : null,
    batch.wt_per_m != null ? `${fmtQty(batch.wt_per_m)} wt/mm` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="space-y-4">
      <PageHeader
        title={batch.batch_no}
        description={[batch.material_name, batch.grade_name, batch.shape_label]
          .filter(Boolean)
          .join(" · ")}
        actions={
          <>
            {batch.batch_status === "in_stock" ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setToLocationId("");
                    setTransferOpen(true);
                  }}
                >
                  <ArrowRightLeft />
                  Transfer
                </Button>
                <Button type="button" size="sm" onClick={() => setQcOpen(true)}>
                  <ClipboardCheck />
                  QC
                </Button>
              </>
            ) : null}
            <Button asChild variant="ghost" size="sm">
              <Link to="/inventory">
                <ArrowLeft className="size-3.5" />
                Inventory
              </Link>
            </Button>
          </>
        }
      />

      <div className="grid gap-3 md:grid-cols-2">
        <section className="rounded-lg border border-border bg-card p-3 space-y-2">
          <h2 className="text-2xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            Stock
          </h2>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            <Meta label="Weight" value={`${fmtQty(batch.weight)} ${batch.uom_code ?? ""}`.trim()} />
            <Meta label="Qty" value={fmtQty(batch.quantity)} />
            <Meta
              label="Location"
              value={
                batch.location_code
                  ? `${batch.location_code}${batch.location_name ? ` · ${batch.location_name}` : ""}`
                  : "—"
              }
            />
            <Meta
              label="Status"
              value={
                <StatusDot tone={batchStatusTone(batch.batch_status)}>
                  {labelFor(BATCH_STATUSES, batch.batch_status)}
                </StatusDot>
              }
            />
            <Meta
              label="Quality"
              value={
                <StatusDot tone={qualityTone(batch.quality_status)}>
                  {labelFor(QUALITY_STATUSES, batch.quality_status)}
                </StatusDot>
              }
            />
            {batch.inspected_at ? (
              <Meta
                label="Inspected"
                value={format(parseISO(batch.inspected_at), "dd MMM yyyy HH:mm")}
              />
            ) : null}
            {batch.qc_notes ? <Meta label="QC notes" value={batch.qc_notes} /> : null}
            <Meta
              label="Received"
              value={format(parseISO(batch.received_at), "dd MMM yyyy HH:mm")}
            />
            {dims ? <Meta label="Dims" value={dims} /> : null}
            {batch.notes ? <Meta label="Notes" value={batch.notes} /> : null}
          </dl>
        </section>

        <section className="rounded-lg border border-border bg-card p-3 space-y-2">
          <h2 className="text-2xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            Source
          </h2>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            <Meta
              label="Vendor"
              value={
                batch.vendor_name
                  ? `${batch.vendor_code ?? ""} · ${batch.vendor_name}`.replace(/^ · /, "")
                  : "—"
              }
            />
            <Meta
              label="PO"
              value={
                batch.purchase_order_id && batch.po_number ? (
                  <Link
                    to="/purchase/orders/$poId"
                    params={{ poId: batch.purchase_order_id }}
                    className="text-primary hover:underline"
                  >
                    {batch.po_number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta
              label="GRN"
              value={
                receipts.length
                  ? receipts.map((r) => r.receipt_no).join(", ")
                  : "—"
              }
            />
            <Meta
              label="Process path"
              value={batch.process_path ? batch.process_path.replace(/>/g, " → ") : "—"}
            />
            <Meta
              label="Last process"
              value={
                batch.last_process_name
                  ? `${batch.last_process_code ?? ""} · ${batch.last_process_name}`.replace(
                      /^ · /,
                      "",
                    )
                  : "—"
              }
            />
            <Meta
              label="Mother"
              value={
                batch.mother_batch_id && batch.mother_batch_no ? (
                  batch.mother_batch_id === batch.id ? (
                    `${batch.mother_batch_no} (self)`
                  ) : (
                    <Link
                      to="/inventory/$batchId"
                      params={{ batchId: batch.mother_batch_id }}
                      className="text-primary hover:underline"
                    >
                      {batch.mother_batch_no}
                    </Link>
                  )
                ) : (
                  "—"
                )
              }
            />
          </dl>
        </section>
      </div>

      {genealogy &&
      (genealogy.parents.length || genealogy.children.length || genealogy.siblings.length) ? (
        <section className="rounded-lg border border-border bg-card p-3 space-y-2">
          <h2 className="text-2xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            Genealogy
          </h2>
          <dl className="grid gap-2 text-xs md:grid-cols-3">
            <div>
              <div className="text-2xs text-muted-foreground">Parents</div>
              {genealogy.parents.length === 0 ? (
                <span>—</span>
              ) : (
                <ul className="space-y-0.5">
                  {genealogy.parents.map((l) => (
                    <li key={l.id}>
                      <Link
                        to="/inventory/$batchId"
                        params={{ batchId: l.parent_batch_id }}
                        className="text-primary hover:underline"
                      >
                        {l.parent_batch_no ?? l.parent_batch_id.slice(0, 8)}
                      </Link>
                      {l.process_code ? (
                        <span className="text-muted-foreground"> · {l.process_code}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="text-2xs text-muted-foreground">Children</div>
              {genealogy.children.length === 0 ? (
                <span>—</span>
              ) : (
                <ul className="space-y-0.5">
                  {genealogy.children.map((l) => (
                    <li key={l.id}>
                      <Link
                        to="/inventory/$batchId"
                        params={{ batchId: l.child_batch_id }}
                        className="text-primary hover:underline"
                      >
                        {l.child_batch_no ?? l.child_batch_id.slice(0, 8)}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="text-2xs text-muted-foreground">Siblings</div>
              {genealogy.siblings.length === 0 ? (
                <span>—</span>
              ) : (
                <ul className="space-y-0.5">
                  {genealogy.siblings.map((s) => (
                    <li key={s.id}>
                      <Link
                        to="/inventory/$batchId"
                        params={{ batchId: s.id }}
                        className="text-primary hover:underline"
                      >
                        {s.batch_no}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </dl>
        </section>
      ) : null}

      <section className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="border-b border-border px-3 py-2">
          <h2 className="text-sm font-semibold">Movements</h2>
          <p className="text-2xs text-muted-foreground">Qty / weight history for this batch</p>
        </div>
        {movesLoading ? (
          <p className="px-3 py-8 text-center text-xs text-muted-foreground">Loading…</p>
        ) : movements.length === 0 ? (
          <p className="px-3 py-8 text-center text-xs text-muted-foreground">No movements yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>When</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead className="text-right">Δ Qty</TableHead>
                <TableHead className="text-right">Δ Wt</TableHead>
                <TableHead className="text-right">After Wt</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {movements.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="tabular-nums text-xs text-muted-foreground">
                    {format(parseISO(m.movement_at), "dd MMM yyyy HH:mm")}
                  </TableCell>
                  <TableCell className="text-xs">{labelFor(MOVEMENT_KINDS, m.kind)}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {fmtSigned(m.quantity_delta)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {fmtSigned(m.weight_delta)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs font-medium">
                    {fmtQty(m.weight_after)}
                  </TableCell>
                  <TableCell className="max-w-[14rem] truncate text-xs text-muted-foreground">
                    {m.notes ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      <VerifyQualitySheet
        open={qcOpen}
        onOpenChange={setQcOpen}
        row={
          {
            id: batch.id,
            batch_no: batch.batch_no,
            material_id: batch.material_id,
            material_name: batch.material_name ?? null,
            grade_name: batch.grade_name ?? null,
            shape_label: batch.shape_label ?? null,
            location_code: batch.location_code ?? null,
            location_name: batch.location_name ?? null,
            quantity: batch.quantity,
            weight: batch.weight,
            quality_status: batch.quality_status,
            batch_status: batch.batch_status,
            received_at: batch.received_at,
            qc_notes: batch.qc_notes,
            inspected_at: batch.inspected_at,
            plan_no: null,
            process_name: null,
            source: batch.purchase_order_id ? "receipt" : "other",
          } satisfies QcQueueRow
        }
      />

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Transfer {batch.batch_no}</DialogTitle>
          </DialogHeader>
          <Field label="To location" required>
            <NativeSelect
              value={toLocationId}
              onChange={(e) => setToLocationId(e.target.value)}
              autoFocus
            >
              <option value="">Select location…</option>
              {locations
                .filter((l) => l.is_active && l.id !== batch.location_id)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.code} · {l.name}
                  </option>
                ))}
            </NativeSelect>
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => setTransferOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!toLocationId || transfer.isPending}
              onClick={async () => {
                await transfer.mutateAsync({ batchId, toLocationId });
                setTransferOpen(false);
              }}
            >
              {transfer.isPending ? "Transferring…" : "Transfer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs text-muted-foreground">{label}</dt>
      <dd className="truncate font-medium">{value}</dd>
    </div>
  );
}

function fmtSigned(n: number) {
  if (!n) return "0";
  const s = fmtQty(Math.abs(n));
  return n > 0 ? `+${s}` : `−${s}`;
}
